import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "secondary-outline"
  | "subtle"
  | "ghost"
  | "danger"
  | "white"
  | "tab";

export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isPill?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isActive?: boolean; // for tab variant
  to?: string; // renders react-router Link
  href?: string; // renders <a>
  as?: React.ElementType; // custom component override
  target?: string;
  rel?: string;
}

/**
 * CoreDesign Unified Button System
 * 
 * Embodies Apple HIG squircle geometry, spring-physics hover elevation,
 * brand color tokens (#3e57da primary, #1e293b secondary), and polymorphic
 * rendering (button, Link, or a tags).
 */
export const Button = forwardRef<any, ButtonProps>(
  (
    {
      children,
      type = "button",
      variant = "primary",
      size = "md",
      className = "",
      disabled = false,
      isLoading = false,
      isPill = false,
      fullWidth = false,
      isActive = false,
      leftIcon = null,
      icon = null,
      rightIcon = null,
      to,
      href,
      as: CustomComponent,
      ...props
    },
    ref
  ) => {
    // Normalise icon prop (supports either leftIcon or icon)
    const effectiveLeftIcon = leftIcon || icon;

    // Base structural classes
    const baseStyles =
      "cht-group cht-inline-flex cht-items-center cht-justify-center cht-font-bold cht-no-underline cht-select-none cht-transition-all cht-duration-200 cht-outline-none focus-visible:cht-ring-2 focus-visible:cht-ring-offset-2";

    // Physics / Motion & Disabled handling
    const interactiveStyles =
      disabled || isLoading
        ? "cht-opacity-60 cht-cursor-not-allowed cht-pointer-events-none"
        : "hover:-cht-translate-y-0.5 active:cht-translate-y-0 active:cht-scale-95";

    // Sizing presets
    const sizes: Record<ButtonSize, string> = {
      xs: "cht-px-2.5 cht-py-1 cht-text-[11px] cht-gap-1",
      sm: "cht-px-4 cht-py-2 cht-text-xs cht-gap-1.5",
      md: "cht-px-5 cht-py-2.5 cht-text-sm cht-gap-2",
      lg: "cht-px-6 sm:cht-px-8 cht-py-3.5 cht-text-base cht-gap-2.5",
    };

    // Color & Elevation Variants
    const variants: Record<ButtonVariant, string> = {
      // Primary: Brand Blue #3e57da with signature navbar glow shadow
      primary:
        "cht-bg-[#3e57da] hover:cht-bg-[#354bc4] active:cht-bg-[#2d41a8] !cht-text-white hover:!cht-text-white cht-shadow-sm hover:cht-shadow-[0_8px_20px_rgba(62,87,218,0.35)] focus-visible:cht-ring-[#3e57da]/60",

      // Secondary: Brand Dark Slate #1e293b with matching dark glow shadow
      secondary:
        "cht-bg-[#1e293b] hover:cht-bg-[#0f172a] active:cht-bg-black !cht-text-white hover:!cht-text-white cht-border cht-border-slate-800/80 cht-shadow-sm hover:cht-shadow-[0_8px_20px_rgba(30,41,59,0.35)] focus-visible:cht-ring-[#1e293b]/60",

      // Outline: Primary border
      outline:
        "cht-bg-transparent cht-border-2 cht-border-[#3e57da] cht-text-[#3e57da] hover:cht-bg-[#3e57da]/10 active:cht-bg-[#3e57da]/15 focus-visible:cht-ring-[#3e57da]/40",

      // Secondary Outline: Slate border
      "secondary-outline":
        "cht-bg-transparent cht-border cht-border-slate-300 hover:cht-border-slate-900 cht-text-[#1e293b] hover:cht-bg-slate-100/80 focus-visible:cht-ring-slate-300",

      // Subtle / Soft: Low-contrast slate background
      subtle:
        "cht-bg-slate-100 hover:cht-bg-slate-200/90 active:cht-bg-slate-300/80 cht-text-[#1e293b] hover:cht-text-[#3e57da] focus-visible:cht-ring-slate-300",

      // Ghost: Completely transparent at rest
      ghost:
        "cht-bg-transparent cht-text-[#1e293b] hover:cht-text-[#3e57da] hover:cht-bg-[#3e57da]/10 focus-visible:cht-ring-slate-200",

      // Danger: Rose red
      danger:
        "cht-bg-rose-600 hover:cht-bg-rose-700 active:cht-bg-rose-800 !cht-text-white hover:!cht-text-white cht-shadow-sm hover:cht-shadow-[0_8px_20px_rgba(225,29,72,0.35)] focus-visible:cht-ring-rose-500/60",

      // White: High-contrast card on dark backgrounds
      white:
        "cht-bg-white hover:cht-bg-slate-100 !cht-text-[#1e293b] hover:!cht-text-[#3e57da] cht-shadow-sm hover:cht-shadow-lg focus-visible:cht-ring-white/80",

      // Tab pills
      tab: isActive
        ? "cht-bg-[#3e57da] !cht-text-white cht-shadow-md"
        : "cht-bg-gray-100 cht-text-gray-500 hover:cht-bg-gray-200 hover:cht-text-gray-800",
    };

    // Border radius logic (Squircle standard: 12px for sm/md, 14-18px for lg)
    const hasCustomRadius =
      className.includes("cht-rounded") || className.includes("rounded");
    const radiusClass = hasCustomRadius
      ? ""
      : isPill
      ? "cht-rounded-full"
      : size === "lg"
      ? "cht-rounded-[18px]"
      : size === "xs"
      ? "cht-rounded-lg"
      : "cht-rounded-xl";

    const widthClass = fullWidth ? "cht-w-full" : "";

    const combinedClasses = [
      baseStyles,
      interactiveStyles,
      sizes[size],
      variants[variant],
      radiusClass,
      widthClass,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Inner content rendering with icon motion micro-interactions
    const content = (
      <>
        {/* Loading Spinner */}
        {isLoading && (
          <Loader2 className="cht-w-4 cht-h-4 cht-animate-spin cht-shrink-0" />
        )}

        {/* Left Icon (scales slightly on hover) */}
        {!isLoading && effectiveLeftIcon && (
          <span className="cht-inline-flex cht-items-center cht-shrink-0 cht-transition-transform cht-duration-200 group-hover:cht-scale-110">
            {effectiveLeftIcon}
          </span>
        )}

        {/* Label */}
        {children && <span>{children}</span>}

        {/* Right Icon (nudges forward on hover) */}
        {!isLoading && rightIcon && (
          <span className="cht-inline-flex cht-items-center cht-shrink-0 cht-transition-transform cht-duration-200 group-hover:cht-translate-x-1">
            {rightIcon}
          </span>
        )}
      </>
    );

    // 1. Polymorphic: React Router Link
    if (to && !disabled) {
      return (
        <Link to={to} className={combinedClasses} ref={ref} {...(props as any)}>
          {content}
        </Link>
      );
    }

    // 2. Polymorphic: External / Standard Anchor
    if (href && !disabled) {
      return (
        <a href={href} className={combinedClasses} ref={ref} {...(props as any)}>
          {content}
        </a>
      );
    }

    // 3. Polymorphic: Custom Component
    if (CustomComponent) {
      return (
        <CustomComponent
          className={combinedClasses}
          ref={ref}
          disabled={disabled || isLoading}
          {...(props as any)}
        >
          {content}
        </CustomComponent>
      );
    }

    // 4. Default: Native HTML button
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        className={combinedClasses}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = "Button";

// Alias exports for project-wide convenience
export const ChtButton = Button;

export const PrimaryButton = forwardRef<any, ButtonProps>((props, ref) => (
  <Button ref={ref} variant="primary" {...props} />
));
PrimaryButton.displayName = "PrimaryButton";

export const SecondaryButton = forwardRef<any, ButtonProps>((props, ref) => (
  <Button ref={ref} variant="secondary" {...props} />
));
SecondaryButton.displayName = "SecondaryButton";

export default Button;
